import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import {
  Box,
  Flex,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  IconButton,
  Badge,
  Card,
  Input,
  Textarea,
  NativeSelect,
  Tabs,
  SimpleGrid,
  useDisclosure,
  Spinner,
  Alert,
  Tag,
  TagLabel,
  Table,
  Link,
  Dialog,
  Portal,
  Separator,
  Field,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { TabList, Tab, TabPanels, TabPanel, Thead, Tbody, Tr, Th, Td, tabsControlledValue, tabsOnValueChange } from '@/components/ui/chakra-v2-compat';
import { toaster } from '@/components/ui/toaster';
import { InputGroup, InputLeftElement } from '@/components/ui/input-group';
import AppLayout from './AppLayout.jsx';
import { useProductTour } from '../hooks/useProductTour';
import { bandaContactosSteps } from '../tours/bandaContactosTour';
import {
  FaPlus, FaEdit, FaTrash, FaPhone, FaEnvelope, FaGlobe, FaSearch,
  FaGuitar, FaBuilding, FaLandmark, FaAddressBook, FaTh, FaList,
  FaSortAlphaDown, FaSortAlphaUpAlt, FaMapMarkerAlt, FaMoneyBillWave,
  FaClock, FaExternalLinkAlt, FaUserFriends,
} from 'react-icons/fa';
import { contactosAPI, instrumentosAPI } from '../services/api';
import { parseNominatimAddress } from '../utils/nominatimAddress.js';

// ── Constantes ────────────────────────────────────────────────────────────────

const SECCIONES_FIJAS = [
  { key: 'REFUERZO',  label: 'Refuerzos',  icon: FaGuitar,    color: 'purple' },
  { key: 'PROVEEDOR', label: 'Proveedores', icon: FaBuilding,  color: 'blue'   },
  { key: 'ENTIDAD',   label: 'Entidades',   icon: FaLandmark,  color: 'teal'   },
];

const TIPO_PERSONALIZADO_LABELS = {
  MUSICAL: { label: 'Musical',  color: 'purple', icon: FaGuitar      },
  EMPRESA: { label: 'Empresa',  color: 'blue',   icon: FaBuilding    },
  ENTIDAD: { label: 'Entidad',  color: 'teal',   icon: FaLandmark    },
  PERSONA: { label: 'Personas', color: 'orange', icon: FaUserFriends },
};

const CATEGORIA_PROVEEDOR_OPTS = [
  'Sonido / PA', 'Iluminación', 'Transporte / Autobús', 'Vestuario / Uniformes',
  'Fotografía / Vídeo', 'Catering', 'Material musical', 'Imprenta / Diseño',
  'Logística', 'Seguridad', 'Otro',
];

const TIPO_ENTIDAD_OPTS = [
  'Ayuntamiento', 'Diputación', 'Comunidad Autónoma', 'Federación de Bandas',
  'Parroquia / Hermandad', 'Asociación cultural', 'Empresa privada',
  'Centro educativo', 'Otro',
];

// ── Helpers ──────────────────────────────────────────────────────────────────

const initForm = (seccion, pestanaId = null) => ({
  seccion, pestanaId,
  nombre: '', apellidos: '', organizacion: '', cargo: '',
  telefono: '', email: '', web: '', notas: '',
  direccion: '', lat: null, lng: null, localidad: '', provincia: '',
  // Array de { instrumentoId?, instrumentoPersonalizadoId?, nombre, familia, voz? }
  instrumentos: [],
  cachet: '', disponibilidad: '',
  categoriaProveedor: '', servicioProveedor: '',
  tipoEntidad: '',
});

const formFromContacto = (c) => ({
  seccion: c.seccion, pestanaId: c.pestanaId || null,
  nombre: c.nombre || '', apellidos: c.apellidos || '',
  organizacion: c.organizacion || '', cargo: c.cargo || '',
  telefono: c.telefono || '', email: c.email || '',
  web: c.web || '', notas: c.notas || '',
  direccion: c.direccion || [c.localidad, c.provincia].filter(Boolean).join(', ') || '',
  lat: c.lat != null ? Number(c.lat) : null,
  lng: c.lng != null ? Number(c.lng) : null,
  localidad: c.localidad || '', provincia: c.provincia || '',
  instrumentos: Array.isArray(c.instrumentos) ? c.instrumentos : [],
  cachet: c.cachet || '', disponibilidad: c.disponibilidad || '',
  categoriaProveedor: c.categoriaProveedor || '', servicioProveedor: c.servicioProveedor || '',
  tipoEntidad: c.tipoEntidad || '',
});

function formatContactoDireccion(c) {
  if (c?.direccion?.trim()) return c.direccion.trim();
  return [c?.localidad, c?.provincia].filter(Boolean).join(', ');
}

function formatNombreCompleto(c) {
  return [c.nombre, c.apellidos].filter(Boolean).join(' ') || '—';
}
function getAvatarLabel(c) {
  return (c.apellidos?.[0] || c.nombre?.[0] || '?').toUpperCase();
}
function getAvatarColor(seccion) {
  const map = { REFUERZO: 'purple.500', PROVEEDOR: 'blue.500', ENTIDAD: 'teal.500', PERSONALIZADO: 'gray.500' };
  return map[seccion] || 'gray.500';
}

// ── Modal de ficha de contacto ────────────────────────────────────────────────

function FichaContactoModal({ contacto: c, isOpen, onClose, onEdit, onDelete }) {
  const bg      = useColorModeValue('gray.50', 'gray.800');
  const cardBg  = useColorModeValue('white', 'gray.700');
  const sub     = useColorModeValue('gray.500', 'gray.400');
  const divCol  = useColorModeValue('gray.200', 'gray.600');

  if (!c) return null;

  const instrumentosArr = Array.isArray(c.instrumentos) ? c.instrumentos : [];

  const DataRow = ({ icon: Icon, label, value, href }) => {
    if (!value) return null;
    return (
      <HStack align="start" gap={3} py={2} borderBottomWidth="1px" borderColor={divCol}>
        <Box color="purple.400" mt={0.5} w={4} flexShrink={0}><Icon size={14} /></Box>
        <Box>
          <Text fontSize="xs" color={sub} mb={0.5}>{label}</Text>
          {href
            ? <Link
            href={href}
            color="purple.500"
            fontSize="sm"
            fontWeight="500"
            target='_blank'
            rel='noopener noreferrer'>{value}</Link>
            : <Text fontSize="sm" fontWeight="500">{value}</Text>}
        </Box>
      </HStack>
    );
  };

  return (
    <Dialog.Root open={isOpen} size='md' scrollBehavior="inside" onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.CloseTrigger />
            <Dialog.Body p={0}>
              {/* Cabecera */}
              <Box bg="purple.500" px={6} pt={10} pb={6} _dark={{ bg: 'purple.800' }}>
                <VStack gap={2} align="center">
                  <Avatar.Root size="xl" bg="white" color="purple.500" fontWeight="700" fontSize="2xl"><Avatar.Fallback name={getAvatarLabel(c)} /></Avatar.Root>
                  <VStack gap={0} align="center">
                    <Text fontWeight="800" fontSize="lg" color="white">{formatNombreCompleto(c)}</Text>
                    {(c.cargo || c.organizacion) && (
                      <Text fontSize="sm" color="purple.100">
                        {[c.cargo, c.organizacion].filter(Boolean).join(' · ')}
                      </Text>
                    )}
                    {formatContactoDireccion(c) && (
                      <HStack gap={1} mt={1}>
                        <FaMapMarkerAlt color="rgba(255,255,255,0.7)" size={11} />
                        <Text fontSize="xs" color="purple.200">
                          {formatContactoDireccion(c)}
                        </Text>
                      </HStack>
                    )}
                  </VStack>
                  <HStack gap={2} flexWrap="wrap" justify="center" mt={1}>
                    {instrumentosArr.map((inst, i) => (
                      <Badge key={i} colorPalette="purple" variant="solid" bg="purple.700" color="white" fontSize="xs">
                        🎵 {inst.nombre}{inst.voz ? ` · ${inst.voz}` : ''}
                      </Badge>
                    ))}
                    {c.categoriaProveedor && (
                      <Badge colorPalette="blue" variant="solid" bg="blue.700" color="white" fontSize="xs">{c.categoriaProveedor}</Badge>
                    )}
                    {c.tipoEntidad && (
                      <Badge colorPalette="teal" variant="solid" bg="teal.700" color="white" fontSize="xs">{c.tipoEntidad}</Badge>
                    )}
                  </HStack>
                </VStack>
              </Box>

              {/* Cuerpo */}
              <Box px={6} py={4}>
                {/* Contacto */}
                <Heading size="xs" color={sub} textTransform="uppercase" letterSpacing="wide" mb={2}>Contacto</Heading>
                <DataRow icon={FaPhone}    label="Teléfono"  value={c.telefono}  href={c.telefono  ? `tel:${c.telefono}` : null} />
                <DataRow icon={FaEnvelope} label="Email"     value={c.email}     href={c.email     ? `mailto:${c.email}` : null} />
                <DataRow icon={FaGlobe}    label="Web"       value={c.web}       href={c.web       ? c.web : null} />
                <DataRow
                  icon={FaMapMarkerAlt}
                  label="Dirección"
                  value={formatContactoDireccion(c)}
                  href={
                    c.lat != null && c.lng != null
                      ? `https://www.openstreetmap.org/?mlat=${c.lat}&mlon=${c.lng}#map=16/${c.lat}/${c.lng}`
                      : formatContactoDireccion(c)
                        ? `https://www.openstreetmap.org/search?query=${encodeURIComponent(formatContactoDireccion(c))}`
                        : null
                  }
                />

                {/* Refuerzo */}
                {(c.cachet || c.disponibilidad) && (
                  <>
                    <Heading size="xs" color={sub} textTransform="uppercase" letterSpacing="wide" mt={4} mb={2}>Condiciones</Heading>
                    <DataRow icon={FaMoneyBillWave} label="Caché / Honorarios" value={c.cachet} />
                    <DataRow icon={FaClock}         label="Disponibilidad"     value={c.disponibilidad} />
                  </>
                )}

                {/* Proveedor */}
                {c.servicioProveedor && (
                  <>
                    <Heading size="xs" color={sub} textTransform="uppercase" letterSpacing="wide" mt={4} mb={2}>Servicio</Heading>
                    <DataRow icon={FaBuilding} label="Descripción del servicio" value={c.servicioProveedor} />
                  </>
                )}

                {/* Notas */}
                {c.notas && (
                  <>
                    <Heading size="xs" color={sub} textTransform="uppercase" letterSpacing="wide" mt={4} mb={2}>Notas</Heading>
                    <Box bg={bg} p={3} rounded="lg">
                      <Text fontSize="sm" fontStyle="italic" color={sub} whiteSpace="pre-wrap">{c.notas}</Text>
                    </Box>
                  </>
                )}
              </Box>
            </Dialog.Body>
            <Dialog.Footer borderTopWidth="1px" borderColor={divCol} gap={2}>
              <Button
                size="sm"
                variant="ghost"
                colorPalette="red"
                onClick={() => { onClose(); onDelete(c); }}><FaTrash />Eliminar
                          </Button>
              <Button
                size="sm"
                colorPalette="purple"
                ml="auto"
                onClick={() => { onClose(); onEdit(c); }}><FaEdit />Editar
                          </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}

// ── Tarjeta mosaico ───────────────────────────────────────────────────────────

function ContactoCard({ contacto: c, onView, onEdit, onDelete }) {
  const cardBg  = useColorModeValue('white', 'gray.800');
  const sub     = useColorModeValue('gray.500', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.750');
  const instrumentosArr = Array.isArray(c.instrumentos) ? c.instrumentos : [];

  return (
    <Card.Root
      bg={cardBg} shadow="sm" rounded="xl"
      _hover={{ shadow: 'md', bg: hoverBg }} transition="all 0.15s"
      cursor="pointer" onClick={() => onView(c)}
    >
      <Card.Body p={4}>
        <HStack align="start" gap={3}>
          <Avatar.Root size="md" bg={getAvatarColor(c.seccion)} color="white"><Avatar.Fallback name={getAvatarLabel(c)} /></Avatar.Root>
          <Box flex="1" minW={0}>
            <Text fontWeight="700" fontSize="sm" lineClamp={1}>{formatNombreCompleto(c)}</Text>
            {c.organizacion && <Text fontSize="xs" color={sub} lineClamp={1}>{c.organizacion}</Text>}
            {c.cargo        && <Text fontSize="xs" color={sub} lineClamp={1}>{c.cargo}</Text>}
            {formatContactoDireccion(c) && (
              <HStack gap={1} mt={0.5}>
                <FaMapMarkerAlt size={9} color="gray" />
                <Text fontSize="xs" color={sub} lineClamp={1}>
                  {formatContactoDireccion(c)}
                </Text>
              </HStack>
            )}
            <HStack mt={1.5} gap={1} flexWrap="wrap">
              {instrumentosArr.map((inst, i) => (
                <Tag.Root key={i} size="sm" colorPalette="purple" variant="subtle">
                  <Tag.Label fontSize="xs">{inst.nombre}{inst.voz ? ` · ${inst.voz}` : ''}</Tag.Label>
                </Tag.Root>
              ))}
              {c.categoriaProveedor && (
                <Tag.Root size="sm" colorPalette="blue" variant="subtle"><Tag.Label fontSize="xs">{c.categoriaProveedor}</Tag.Label></Tag.Root>
              )}
              {c.tipoEntidad && (
                <Tag.Root size="sm" colorPalette="teal" variant="subtle"><Tag.Label fontSize="xs">{c.tipoEntidad}</Tag.Label></Tag.Root>
              )}
            </HStack>
            <HStack mt={2} gap={3}>
              {c.telefono && (
                <HStack gap={1} color={sub}>
                  <FaPhone size={9} /><Text fontSize="xs">{c.telefono}</Text>
                </HStack>
              )}
              {c.email && (
                <HStack gap={1} color={sub}>
                  <FaEnvelope size={9} /><Text fontSize="xs" lineClamp={1}>{c.email}</Text>
                </HStack>
              )}
            </HStack>
          </Box>
          <VStack gap={1} flexShrink={0}>
            <IconButton
              size="xs"
              variant="ghost"
              colorPalette="purple"
              aria-label="Editar"
              onClick={e => { e.stopPropagation(); onEdit(c); }}><FaEdit /></IconButton>
            <IconButton
              size="xs"
              variant="ghost"
              colorPalette="red"
              aria-label="Eliminar"
              onClick={e => { e.stopPropagation(); onDelete(c); }}><FaTrash /></IconButton>
          </VStack>
        </HStack>
        {c.notas && (
          <Text fontSize="xs" color={sub} mt={2} lineClamp={2} fontStyle="italic">{c.notas}</Text>
        )}
      </Card.Body>
    </Card.Root>
  );
}

// ── Fila de lista ─────────────────────────────────────────────────────────────

function ContactoListRow({ contacto: c, onView, onEdit, onDelete }) {
  const sub       = useColorModeValue('gray.500', 'gray.400');
  const hoverBg   = useColorModeValue('gray.50', 'gray.750');
  const instrumentosArr = Array.isArray(c.instrumentos) ? c.instrumentos : [];

  return (
    <Table.Row _hover={{ bg: hoverBg }} cursor="pointer" onClick={() => onView(c)}>
      <Table.Cell>
        <HStack gap={3}>
          <Avatar.Root size="sm" bg={getAvatarColor(c.seccion)} color="white"><Avatar.Fallback name={getAvatarLabel(c)} /></Avatar.Root>
          <Box minW={0}>
            <Text fontWeight="600" fontSize="sm" lineClamp={1}>{formatNombreCompleto(c)}</Text>
            {c.organizacion && <Text fontSize="xs" color={sub} lineClamp={1}>{c.organizacion}</Text>}
          </Box>
        </HStack>
      </Table.Cell>
      <Table.Cell fontSize="xs" color={sub}>
        {instrumentosArr.length > 0
          ? instrumentosArr.map(i => `${i.nombre}${i.voz ? ` · ${i.voz}` : ''}`).join(', ')
          : c.categoriaProveedor || c.tipoEntidad || '—'}
      </Table.Cell>
      <Table.Cell fontSize="xs" color={sub}>
        {formatContactoDireccion(c) || '—'}
      </Table.Cell>
      <Table.Cell fontSize="xs">
        <VStack align="start" gap={0.5}>
          {c.telefono && (
            <Link href={`tel:${c.telefono}`} onClick={e => e.stopPropagation()}
              color="purple.500" _hover={{ textDecor: 'underline' }}>
              {c.telefono}
            </Link>
          )}
          {c.email && (
            <Link href={`mailto:${c.email}`} onClick={e => e.stopPropagation()}
              color="purple.500" _hover={{ textDecor: 'underline' }} lineClamp={1}>
              {c.email}
            </Link>
          )}
        </VStack>
      </Table.Cell>
      <Table.Cell onClick={e => e.stopPropagation()}>
        <HStack gap={1}>
          <IconButton
            size="xs"
            variant="ghost"
            colorPalette="purple"
            aria-label="Editar"
            onClick={() => onEdit(c)}><FaEdit /></IconButton>
          <IconButton
            size="xs"
            variant="ghost"
            colorPalette="red"
            aria-label="Eliminar"
            onClick={() => onDelete(c)}><FaTrash /></IconButton>
        </HStack>
      </Table.Cell>
    </Table.Row>
  );
}

// ── Formulario de contacto ────────────────────────────────────────────────────

function ContactoForm({ form, setForm, instrumentos, seccion }) {
  const isRefuerzo  = seccion === 'REFUERZO';
  const isProveedor = seccion === 'PROVEEDOR';
  const isEntidad   = seccion === 'ENTIDAD';
  const isPersonal  = seccion === 'PERSONALIZADO';
  const suggestBg = useColorModeValue('white', 'gray.800');
  const suggestBorder = useColorModeValue('gray.200', 'gray.600');
  const [sugerencias, setSugerencias] = useState([]);
  const [buscandoDir, setBuscandoDir] = useState(false);
  const debounceRef = useRef(null);

  const field = (key) => ({
    value: form[key] ?? '',
    onChange: (e) => setForm(f => ({ ...f, [key]: e.target.value })),
  });

  const buscarDireccion = (valor) => {
    setForm(f => ({ ...f, direccion: valor, lat: null, lng: null, localidad: '', provincia: '' }));
    setSugerencias([]);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (valor.trim().length < 3) return;
    debounceRef.current = setTimeout(async () => {
      setBuscandoDir(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(valor)}&limit=5&countrycodes=es&addressdetails=1`,
          { headers: { 'Accept-Language': 'es' } },
        );
        const data = await res.json();
        setSugerencias(Array.isArray(data) ? data : []);
      } catch {
        setSugerencias([]);
      } finally {
        setBuscandoDir(false);
      }
    }, 400);
  };

  const seleccionarSugerencia = (item) => {
    const parsed = parseNominatimAddress(item);
    setForm(f => ({
      ...f,
      direccion: item.display_name || '',
      lat: parsed.lat,
      lng: parsed.lng,
      localidad: parsed.localizacion.ciudad || '',
      provincia: parsed.localizacion.provincia || '',
    }));
    setSugerencias([]);
  };

  return (
    <VStack gap={3} align="stretch">
      <SimpleGrid columns={2} gap={3}>
        <Field.Root required>
          <Field.Label fontSize="sm">Nombre</Field.Label>
          <Input size="sm" placeholder="Nombre" {...field('nombre')} />
        </Field.Root>
        <Field.Root>
          <Field.Label fontSize="sm">Apellidos</Field.Label>
          <Input size="sm" placeholder="Apellidos" {...field('apellidos')} />
        </Field.Root>
      </SimpleGrid>
      <SimpleGrid columns={2} gap={3}>
        <Field.Root>
          <Field.Label fontSize="sm">Teléfono</Field.Label>
          <Input size="sm" type="tel" placeholder="000 000 000" {...field('telefono')} />
        </Field.Root>
        <Field.Root>
          <Field.Label fontSize="sm">Email</Field.Label>
          <Input size="sm" type="email" placeholder="email@ejemplo.com" {...field('email')} />
        </Field.Root>
      </SimpleGrid>
      <Field.Root>
        <Field.Label fontSize="sm">Web</Field.Label>
        <Input size="sm" type="url" placeholder="https://..." {...field('web')} />
      </Field.Root>
      <Field.Root w="full">
        <Field.Label fontSize="sm">Dirección</Field.Label>
        <Box position="relative" w="full">
          <InputGroup size="sm" w="full">
            <InputLeftElement pointerEvents="none">
              {buscandoDir
                ? <Spinner size="xs" color="purple.400" />
                : <FaMapMarkerAlt color="gray" size={12} />}
            </InputLeftElement>
            <Input
              w="full"
              pl={9}
              value={form.direccion || ''}
              onChange={e => buscarDireccion(e.target.value)}
              placeholder="Empieza a escribir la dirección…"
              autoComplete="off"
              rounded="lg"
            />
          </InputGroup>
          {sugerencias.length > 0 && (
            <Box
              position="absolute"
              top="100%"
              left={0}
              right={0}
              zIndex={1000}
              bg={suggestBg}
              borderWidth="1px"
              borderColor={suggestBorder}
              rounded="xl"
              shadow="lg"
              mt={1}
              overflow="hidden"
            >
              {sugerencias.map((s, i) => (
                <Box
                  key={`${s.place_id || i}-${s.lat}`}
                  px={3}
                  py={2}
                  cursor="pointer"
                  _hover={{ bg: 'purple.50' }}
                  borderBottom={i < sugerencias.length - 1 ? '1px solid' : 'none'}
                  borderBottomColor={suggestBorder}
                  onClick={() => seleccionarSugerencia(s)}
                >
                  <Text fontSize="sm" lineClamp={2}>📍 {s.display_name}</Text>
                </Box>
              ))}
            </Box>
          )}
        </Box>
        {form.lat != null && form.lng != null && Number.isFinite(Number(form.lat)) && Number.isFinite(Number(form.lng)) ? (
          <Text fontSize="xs" color="purple.600" mt={1}>
            ✓ Ubicación encontrada ({Number(form.lat).toFixed(5)}, {Number(form.lng).toFixed(5)})
          </Text>
        ) : (
          <Field.HelperText fontSize="xs">
            Elige una sugerencia para guardar tu dirección.
          </Field.HelperText>
        )}
      </Field.Root>

      <Separator />

      {(isRefuerzo || isPersonal) && (() => {
        const instList = Array.isArray(form.instrumentos) ? form.instrumentos : [];

        const addInstrumento = (instrumentoId) => {
          if (!instrumentoId) return;
          const found = (instrumentos || []).find(i => i.id === instrumentoId);
          if (!found) return;
          if (instList.some(i => i.instrumentoId === instrumentoId)) return; // ya existe
          setForm(f => ({
            ...f,
            instrumentos: [...instList, { instrumentoId: found.id, nombre: found.nombre, familia: found.familia, voz: '' }],
          }));
        };

        const removeInstrumento = (idx) => {
          setForm(f => ({ ...f, instrumentos: instList.filter((_, i) => i !== idx) }));
        };

        const updateVoz = (idx, voz) => {
          setForm(f => ({ ...f, instrumentos: instList.map((inst, i) => i === idx ? { ...inst, voz } : inst) }));
        };

        return (
          <>
            <Heading size="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">Datos musicales</Heading>

            {/* Lista de instrumentos añadidos */}
            {instList.length > 0 && (
              <VStack gap={2} align="stretch">
                {instList.map((inst, idx) => (
                  <HStack key={idx} gap={2} bg="purple.50" _dark={{ bg: 'purple.900' }} p={2} rounded="md">
                    <Badge colorPalette="purple" fontSize="xs" flexShrink={0}>{inst.nombre}</Badge>
                    <Input
                      size="xs" placeholder="Voz / parte (1ª, 2ª…)" value={inst.voz || ''}
                      onChange={e => updateVoz(idx, e.target.value)}
                      maxW="140px" bg="white" _dark={{ bg: 'gray.700' }} rounded="md"
                    />
                    <IconButton
                      size="xs"
                      colorPalette="red"
                      variant="ghost"
                      aria-label="Quitar instrumento"
                      onClick={() => removeInstrumento(idx)}><FaTrash /></IconButton>
                  </HStack>
                ))}
              </VStack>
            )}

            {/* Selector para añadir un instrumento */}
            <HStack gap={2}>
              <NativeSelect.Root>
                <NativeSelect.Field
                  size="sm"
                  placeholder="Añadir instrumento…"
                  flex="1"
                  onChange={e => { addInstrumento(e.target.value); e.target.value = ''; }}>
                  {(instrumentos || [])
                    .filter(i => !instList.some(x => x.instrumentoId === i.id))
                    .map(i => <option key={i.id} value={i.id}>{i.nombre}</option>)}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </HStack>

            <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
              <Field.Root>
                <Field.Label fontSize="sm">Caché / Honorarios</Field.Label>
                <Input size="sm" placeholder="Ej: 80€/actuación" {...field('cachet')} />
              </Field.Root>
              <Field.Root>
                <Field.Label fontSize="sm">Disponibilidad</Field.Label>
                <Input size="sm" placeholder="Ej: Fines de semana" {...field('disponibilidad')} />
              </Field.Root>
            </SimpleGrid>
          </>
        );
      })()}

      {(isProveedor || isPersonal) && (
        <>
          {isProveedor && <Heading size="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">Datos de proveedor</Heading>}
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
            <Field.Root>
              <Field.Label fontSize="sm">Categoría</Field.Label>
              <NativeSelect.Root>
                <NativeSelect.Field
                  size="sm"
                  placeholder="Categoría de servicio"
                  {...field('categoriaProveedor')}>
                  {CATEGORIA_PROVEEDOR_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Field.Root>
            <Field.Root>
              <Field.Label fontSize="sm">Servicio / Descripción</Field.Label>
              <Input size="sm" placeholder="Qué ofrece" {...field('servicioProveedor')} />
            </Field.Root>
          </SimpleGrid>
        </>
      )}

      {(isEntidad || isPersonal) && (
        <>
          {isEntidad && <Heading size="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">Datos de entidad</Heading>}
          <Field.Root>
            <Field.Label fontSize="sm">Tipo de entidad</Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field size="sm" placeholder="Tipo de entidad" {...field('tipoEntidad')}>
                {TIPO_ENTIDAD_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>
        </>
      )}

      <Separator />
      <Field.Root>
        <Field.Label fontSize="sm">Notas</Field.Label>
        <Textarea size="sm" rows={3} placeholder="Observaciones, condiciones especiales…" {...field('notas')} />
      </Field.Root>
    </VStack>
  );
}

// ── Panel de contactos ────────────────────────────────────────────────────────

function ContactosPanel({ seccion, pestanaId = null, instrumentos }) {
  const [contactos, setContactos] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);

  // Vista
  const [viewMode, setViewMode]   = useState('mosaico'); // 'mosaico' | 'lista'

  // Filtros
  const [search,          setSearch]          = useState('');
  const [filtroInstr,     setFiltroInstr]     = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroEntidad,   setFiltroEntidad]   = useState('');
  const [filtroLocalidad, setFiltroLocalidad] = useState('');
  const [ordenar,         setOrdenar]         = useState('nombre_asc');

  // CRUD
  const [form,     setForm]     = useState(initForm(seccion, pestanaId));
  const [editing,  setEditing]  = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [viewing,  setViewing]  = useState(null);

  const { open: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure();
  const { open: isDelOpen,  onOpen: onDelOpen,  onClose: onDelClose  } = useDisclosure();
  const { open: isViewOpen, onOpen: onViewOpen, onClose: onViewClose  } = useDisclosure();
  const cancelDelRef = useRef();
  

  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tableBg     = useColorModeValue('white', 'gray.800');
  const toolbarBg   = useColorModeValue('gray.50', 'gray.750');
  const borderSubtle = useColorModeValue('gray.200', 'gray.600');
  const subtle      = useColorModeValue('gray.500', 'gray.400');

  const isRefuerzo  = seccion === 'REFUERZO';
  const isProveedor = seccion === 'PROVEEDOR';
  const isEntidad   = seccion === 'ENTIDAD';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = pestanaId ? { pestanaId } : { seccion };
      const res = await contactosAPI.getContactos(params);
      setContactos(res.data || []);
    } catch {
      toaster.create({ title: 'Error al cargar contactos', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [seccion, pestanaId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Resetear filtros al cambiar de sección
  useEffect(() => {
    setSearch(''); setFiltroInstr(''); setFiltroCategoria('');
    setFiltroEntidad(''); setFiltroLocalidad(''); setOrdenar('nombre_asc');
  }, [seccion, pestanaId]);

  const openNew   = () => { setForm(initForm(seccion, pestanaId)); setEditing(null); onFormOpen(); };
  const openEdit  = (c) => { setForm(formFromContacto(c)); setEditing(c); onFormOpen(); };
  const openView  = (c) => { setViewing(c); onViewOpen(); };
  const openDelete = (c) => { setToDelete(c); onDelOpen(); };

  const handleSave = async () => {
    if (!form.nombre.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form, pestanaId: pestanaId || null, seccion };
      if (editing) {
        await contactosAPI.updateContacto(editing.id, payload);
        toaster.create({ title: 'Contacto actualizado', type: 'success', duration: 2000 });
      } else {
        await contactosAPI.createContacto(payload);
        toaster.create({ title: 'Contacto añadido', type: 'success', duration: 2000 });
      }
      onFormClose();
      load();
    } catch {
      toaster.create({ title: 'Error al guardar', type: 'error' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await contactosAPI.deleteContacto(toDelete.id);
      toaster.create({ title: 'Contacto eliminado', type: 'info', duration: 2000 });
      onDelClose();
      load();
    } catch { toaster.create({ title: 'Error al eliminar', type: 'error' }); }
  };

  // Filtrado + ordenación
  const filtered = useMemo(() => {
    let list = contactos.filter(c => {
      const instArr = Array.isArray(c.instrumentos) ? c.instrumentos : [];
      const instNombres = instArr.map(i => i.nombre).filter(Boolean);
      const q = search.trim().toLowerCase();
      if (q && ![c.nombre, c.apellidos, c.organizacion, c.cargo,
        ...instNombres, c.telefono, c.email, c.direccion, c.localidad, c.provincia]
        .filter(Boolean).some(v => v.toLowerCase().includes(q))) return false;
      if (filtroInstr && !instArr.some(i => i.instrumentoId === filtroInstr)) return false;
      if (filtroCategoria && c.categoriaProveedor !== filtroCategoria) return false;
      if (filtroEntidad   && c.tipoEntidad !== filtroEntidad) return false;
      if (filtroLocalidad) {
        const loc = [c.direccion, c.localidad, c.provincia].filter(Boolean).join(' ').toLowerCase();
        if (!loc.includes(filtroLocalidad.toLowerCase())) return false;
      }
      return true;
    });

    const firstInstNombre = (c) => {
      const arr = Array.isArray(c.instrumentos) ? c.instrumentos : [];
      return arr[0]?.nombre || c.categoriaProveedor || c.tipoEntidad || 'zzz';
    };

    list = [...list].sort((a, b) => {
      switch (ordenar) {
        case 'nombre_asc':
          return formatNombreCompleto(a).localeCompare(formatNombreCompleto(b), 'es');
        case 'nombre_desc':
          return formatNombreCompleto(b).localeCompare(formatNombreCompleto(a), 'es');
        case 'localidad':
          return (formatContactoDireccion(a) || 'zzz')
            .localeCompare(formatContactoDireccion(b) || 'zzz', 'es');
        case 'instrumento':
          return firstInstNombre(a).localeCompare(firstInstNombre(b), 'es');
        default: return 0;
      }
    });

    return list;
  }, [contactos, search, filtroInstr, filtroCategoria, filtroEntidad, filtroLocalidad, ordenar]);

  const hasFilters = search || filtroInstr || filtroCategoria || filtroEntidad || filtroLocalidad;

  return (
    <Box>
      {/* ── Barra de herramientas ── */}
      <Box
        bg={toolbarBg}
        borderWidth="1px"
        borderColor={borderSubtle}
        rounded="xl"
        p={3}
        mb={3}
      >
        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={3}
          align={{ md: 'flex-end' }}
          wrap="wrap"
        >
          <Field.Root flex="1" minW="160px" maxW={{ md: '240px' }} w={{ base: 'full', md: 'auto' }}>
            <Field.Label fontSize="xs" mb={1} fontWeight="600" color={subtle}>
              Buscar
            </Field.Label>
            <InputGroup size="sm" w="full">
              <InputLeftElement pointerEvents="none"><FaSearch color="gray" size={11} /></InputLeftElement>
              <Input placeholder="Nombre, organización…" value={search} onChange={e => setSearch(e.target.value)} rounded="lg" />
            </InputGroup>
          </Field.Root>

          {isRefuerzo && (
            <Field.Root w={{ base: 'full', md: 'auto' }} minW="160px" maxW="200px">
              <Field.Label fontSize="xs" mb={1} fontWeight="600" color={subtle}>
                Instrumento
              </Field.Label>
              <NativeSelect.Root w="full">
                <NativeSelect.Field
                  size="sm"
                  rounded="lg"
                  value={filtroInstr}
                  onChange={e => setFiltroInstr(e.target.value)}
                  placeholder="Todos"
                >
                  {instrumentos.map(i => <option key={i.id} value={i.id}>{i.nombre}</option>)}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Field.Root>
          )}

          {isProveedor && (
            <Field.Root w={{ base: 'full', md: 'auto' }} minW="160px" maxW="220px">
              <Field.Label fontSize="xs" mb={1} fontWeight="600" color={subtle}>
                Categoría
              </Field.Label>
              <NativeSelect.Root w="full">
                <NativeSelect.Field
                  size="sm"
                  rounded="lg"
                  value={filtroCategoria}
                  onChange={e => setFiltroCategoria(e.target.value)}
                  placeholder="Todas"
                >
                  {CATEGORIA_PROVEEDOR_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Field.Root>
          )}

          {isEntidad && (
            <Field.Root w={{ base: 'full', md: 'auto' }} minW="160px" maxW="220px">
              <Field.Label fontSize="xs" mb={1} fontWeight="600" color={subtle}>
                Tipo de entidad
              </Field.Label>
              <NativeSelect.Root w="full">
                <NativeSelect.Field
                  size="sm"
                  rounded="lg"
                  value={filtroEntidad}
                  onChange={e => setFiltroEntidad(e.target.value)}
                  placeholder="Todos"
                >
                  {TIPO_ENTIDAD_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Field.Root>
          )}

          <Field.Root w={{ base: 'full', md: 'auto' }} minW="140px" maxW="180px">
            <Field.Label fontSize="xs" mb={1} fontWeight="600" color={subtle}>
              Dirección
            </Field.Label>
            <Input
              size="sm"
              w="full"
              placeholder="Filtrar…"
              value={filtroLocalidad}
              onChange={e => setFiltroLocalidad(e.target.value)}
              rounded="lg"
            />
          </Field.Root>

          <Field.Root w={{ base: 'full', md: 'auto' }} minW="160px" maxW="200px">
            <Field.Label fontSize="xs" mb={1} fontWeight="600" color={subtle}>
              Ordenar por
            </Field.Label>
            <NativeSelect.Root w="full">
              <NativeSelect.Field
                size="sm"
                rounded="lg"
                value={ordenar}
                onChange={e => setOrdenar(e.target.value)}
              >
                <option value="nombre_asc">Nombre A→Z</option>
                <option value="nombre_desc">Nombre Z→A</option>
                <option value="localidad">Dirección</option>
                <option value="instrumento">
                  {isRefuerzo ? 'Instrumento' : isProveedor ? 'Categoría' : 'Tipo'}
                </option>
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>

          {hasFilters && (
            <Button
              size="sm"
              variant="ghost"
              colorPalette="gray"
              alignSelf={{ md: 'flex-end' }}
              flexShrink={0}
              onClick={() => { setSearch(''); setFiltroInstr(''); setFiltroCategoria(''); setFiltroEntidad(''); setFiltroLocalidad(''); }}
            >
              Limpiar
            </Button>
          )}

          <HStack
            gap={0}
            border="1px solid"
            borderColor={borderColor}
            rounded="md"
            overflow="hidden"
            flexShrink={0}
            alignSelf={{ md: 'flex-end' }}
            ml={{ md: 'auto' }}
          >
            <IconButton
              size="sm"
              variant={viewMode === 'mosaico' ? 'solid' : 'ghost'}
              colorPalette={viewMode === 'mosaico' ? 'purple' : 'gray'}
              aria-label="Mosaico"
              rounded="none"
              onClick={() => setViewMode('mosaico')}><FaTh /></IconButton>
            <IconButton
              size="sm"
              variant={viewMode === 'lista' ? 'solid' : 'ghost'}
              colorPalette={viewMode === 'lista' ? 'purple' : 'gray'}
              aria-label="Lista"
              rounded="none"
              onClick={() => setViewMode('lista')}><FaList /></IconButton>
          </HStack>

          <Button
            size="sm"
            colorPalette="purple"
            flexShrink={0}
            alignSelf={{ md: 'flex-end' }}
            onClick={openNew}
            data-tour="contactos-anadir"
          ><FaPlus />Añadir contacto
          </Button>
        </Flex>
      </Box>

      {/* Contador */}
      {!loading && (
        <Text fontSize="xs" color="gray.500" mb={3}>
          {filtered.length} contacto{filtered.length !== 1 ? 's' : ''}
          {hasFilters ? ' (filtrados)' : ''}
        </Text>
      )}

      {/* ── Contenido ── */}
      {loading ? (
        <Flex justify="center" py={12}><Spinner color="purple.500" /></Flex>
      ) : filtered.length === 0 ? (
        <Alert.Root status="info" rounded="lg">
          <Alert.Indicator />
          {hasFilters ? 'Ningún contacto coincide con los filtros.' : 'Aún no hay contactos en esta sección.'}
        </Alert.Root>
      ) : viewMode === 'mosaico' ? (
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap={3}>
          {filtered.map(c => (
            <ContactoCard key={c.id} contacto={c} onView={openView} onEdit={openEdit} onDelete={openDelete} />
          ))}
        </SimpleGrid>
      ) : (
        <Box bg={tableBg} rounded="xl" shadow="sm" overflow="hidden" borderWidth="1px" borderColor={borderColor}>
          <Box overflowX="auto">
            <Table.Root size="sm" variant="simple">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Nombre</Table.ColumnHeader>
                  <Table.ColumnHeader>{isRefuerzo ? 'Instrumento' : isProveedor ? 'Categoría' : 'Tipo'}</Table.ColumnHeader>
                  <Table.ColumnHeader>Dirección</Table.ColumnHeader>
                  <Table.ColumnHeader>Contacto</Table.ColumnHeader>
                  <Table.ColumnHeader w="80px" />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filtered.map(c => (
                  <ContactoListRow key={c.id} contacto={c} onView={openView} onEdit={openEdit} onDelete={openDelete} />
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        </Box>
      )}

      {/* ── Ficha de contacto ── */}
      <FichaContactoModal
        contacto={viewing}
        open={isViewOpen}
        onClose={onViewClose}
        onEdit={openEdit}
        onDelete={openDelete}
      />

      {/* ── Modal formulario ── */}
      <Dialog.Root open={isFormOpen} size='xl' scrollBehavior="inside" onOpenChange={e => {
        if (!e.open) {
          onFormClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>{editing ? 'Editar contacto' : 'Nuevo contacto'}</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <ContactoForm form={form} setForm={setForm} instrumentos={instrumentos} seccion={seccion} />
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={onFormClose}>Cancelar</Button>
                <Button colorPalette="purple" onClick={handleSave} loading={saving} disabled={!form.nombre.trim()}>
                  {editing ? 'Guardar cambios' : 'Añadir'}
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* ── Confirmación eliminar ── */}
      <Dialog.Root
        open={isDelOpen}
        initialFocusEl={() => cancelDelRef.current}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            onDelClose();
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header>Eliminar contacto</Dialog.Header>
                <Dialog.Body>
                  ¿Eliminar a <strong>{toDelete ? formatNombreCompleto(toDelete) : ''}</strong>? Esta acción no se puede deshacer.
                </Dialog.Body>
                <Dialog.Footer>
                  <Button ref={cancelDelRef} onClick={onDelClose}>Cancelar</Button>
                  <Button colorPalette="red" ml={3} onClick={handleDelete}>Eliminar</Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>
    </Box>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────

export default function Contactos() {
  useProductTour('banda-contactos', bandaContactosSteps, { autoStart: false });
  const [pestanas,     setPestanas]     = useState([]);
  const [loadingP,     setLoadingP]     = useState(true);
  const [instrumentos, setInstrumentos] = useState([]);
  const [tabIndex,     setTabIndex]     = useState(0);

  const { open: isNewTabOpen, onOpen: onNewTabOpen, onClose: onNewTabClose } = useDisclosure();
  const [newTabForm, setNewTabForm] = useState({ nombre: '', tipo: 'MUSICAL' });
  const [savingTab,  setSavingTab]  = useState(false);

  const { open: isDelTabOpen, onOpen: onDelTabOpen, onClose: onDelTabClose } = useDisclosure();
  const [toDelTab,       setToDelTab]       = useState(null);
  const cancelDelTabRef = useRef();

  
  const bg     = useColorModeValue('gray.50', 'gray.900');

  const loadPestanas = useCallback(async () => {
    setLoadingP(true);
    try {
      const res = await contactosAPI.getPestanas();
      setPestanas(res.data || []);
    } catch {
      toaster.create({ title: 'Error al cargar pestañas', type: 'error' });
    } finally { setLoadingP(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadPestanas();
    instrumentosAPI.getAll().then(r => setInstrumentos(r.data || [])).catch(() => {});
  }, [loadPestanas]);

  const handleCreateTab = async () => {
    if (!newTabForm.nombre.trim()) return;
    setSavingTab(true);
    try {
      await contactosAPI.createPestana(newTabForm);
      toaster.create({ title: 'Categoría creada', type: 'success', duration: 2000 });
      onNewTabClose();
      setNewTabForm({ nombre: '', tipo: 'MUSICAL' });
      loadPestanas();
    } catch {
      toaster.create({ title: 'Error al crear categoría', type: 'error' });
    } finally { setSavingTab(false); }
  };

  const handleDeleteTab = async () => {
    if (!toDelTab) return;
    try {
      await contactosAPI.deletePestana(toDelTab.id);
      toaster.create({ title: 'Categoría eliminada', type: 'info', duration: 2000 });
      onDelTabClose();
      setToDelTab(null);
      setTabIndex(0);
      loadPestanas();
    } catch { toaster.create({ title: 'Error al eliminar categoría', type: 'error' }); }
  };

  const allTabs = [
    ...SECCIONES_FIJAS,
    ...pestanas.map(p => ({
      key: p.id, label: p.nombre,
      icon: TIPO_PERSONALIZADO_LABELS[p.tipo]?.icon || FaUserFriends,
      color: TIPO_PERSONALIZADO_LABELS[p.tipo]?.color || 'gray',
      pestanaId: p.id, tipo: p.tipo,
    })),
  ];
  const TOTAL_FIXED = SECCIONES_FIJAS.length;

  return (
    <AppLayout userType="banda">
      <Box minH="100vh" bg={bg} px={{ base: 3, md: 6 }} py={6}>
        {/* Cabecera */}
        <HStack mb={6} justify="space-between" wrap="wrap" gap={3} data-tour="contactos-header">
          <HStack gap={3}>
            <Box p={2} bg="teal.100" rounded="lg" _dark={{ bg: 'teal.900' }}>
              <FaAddressBook size={22} color="#319795" />
            </Box>
            <VStack align="start" gap={0}>
              <Heading size="md">Contactos</Heading>
              <Text fontSize="sm" color="gray.500">Agenda de refuerzos, proveedores y entidades</Text>
            </VStack>
          </HStack>
          <Button size="sm" colorPalette="teal" variant="outline" onClick={onNewTabOpen} data-tour="contactos-categoria"><FaPlus />Nueva categoría
                      </Button>
        </HStack>

        {loadingP ? (
          <Flex justify="center" py={16}><Spinner color="teal.500" size="xl" /></Flex>
        ) : (
          <Tabs.Root value={tabsControlledValue(tabIndex)} onValueChange={tabsOnValueChange(setTabIndex)} variant='subtle' colorPalette="teal" lazyMount>
            <Box overflowX="auto" pb={2}>
              <Tabs.List gap={1} flexWrap="nowrap" minW="max-content" data-tour="contactos-pestanas">
                {allTabs.map((t, idx) => (
                  <Tab value={String(idx)} key={t.key} fontWeight="600" fontSize="sm" px={4}
                    _selected={{ bg: `${t.color}.100`, color: `${t.color}.700`, _dark: { bg: `${t.color}.900`, color: `${t.color}.300` } }}>
                    <HStack gap={2}>
                      <t.icon size={13} />
                      <Text>{t.label}</Text>
                      {idx >= TOTAL_FIXED && (
                        <Badge colorPalette={TIPO_PERSONALIZADO_LABELS[t.tipo]?.color} fontSize="2xs" variant="subtle">
                          {TIPO_PERSONALIZADO_LABELS[t.tipo]?.label}
                        </Badge>
                      )}
                    </HStack>
                  </Tab>
                ))}
              </Tabs.List>
            </Box>

            <TabPanels mt={4}>
              {allTabs.map((t, idx) => (
                <TabPanel value={String(idx)} key={t.key} px={0}>
                  {idx >= TOTAL_FIXED && (
                    <HStack mb={4} justify="space-between">
                      <Badge colorPalette={t.color} fontSize="sm" px={2} py={1} rounded="md">
                        {TIPO_PERSONALIZADO_LABELS[t.tipo]?.label || 'Personalizado'}
                      </Badge>
                      <Button
                        size="xs"
                        colorPalette="red"
                        variant="ghost"
                        onClick={() => { setToDelTab(allTabs[idx]); onDelTabOpen(); }}><FaTrash />Eliminar categoría
                                              </Button>
                    </HStack>
                  )}
                  <ContactosPanel
                    seccion={t.pestanaId ? 'PERSONALIZADO' : t.key}
                    pestanaId={t.pestanaId || null}
                    instrumentos={instrumentos}
                  />
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs.Root>
        )}

        {/* Modal nueva categoría */}
        <Dialog.Root open={isNewTabOpen} size='sm' onOpenChange={e => {
          if (!e.open) {
            onNewTabClose();
          }
        }}>
          <Portal>

            <Dialog.Backdrop />
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header>Nueva categoría de contactos</Dialog.Header>
                <Dialog.CloseTrigger />
                <Dialog.Body>
                  <VStack gap={4}>
                    <Field.Root required>
                      <Field.Label>Nombre de la categoría</Field.Label>
                      <Input placeholder="Ej: Mayordomos, Patrocinadores…"
                        value={newTabForm.nombre} onChange={e => setNewTabForm(f => ({ ...f, nombre: e.target.value }))} />
                    </Field.Root>
                    <Field.Root>
                      <Field.Label>Tipo de contacto</Field.Label>
                      <NativeSelect.Root>
                        <NativeSelect.Field
                          value={newTabForm.tipo}
                          onChange={e => setNewTabForm(f => ({ ...f, tipo: e.target.value }))}>
                          <option value="PERSONA">Persona (colaboradores, utilleros…)</option>
                          <option value="MUSICAL">Musical (refuerzos, músicos)</option>
                          <option value="EMPRESA">Empresa / Proveedor</option>
                          <option value="ENTIDAD">Entidad / Institución</option>
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </Field.Root>
                  </VStack>
                </Dialog.Body>
                <Dialog.Footer>
                  <Button variant="ghost" mr={3} onClick={onNewTabClose}>Cancelar</Button>
                  <Button colorPalette="teal" onClick={handleCreateTab} loading={savingTab} disabled={!newTabForm.nombre.trim()}>
                    Crear
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>

          </Portal>
        </Dialog.Root>

        {/* Confirmar eliminar categoría */}
        <Dialog.Root
          open={isDelTabOpen}
          initialFocusEl={() => cancelDelTabRef.current}
          role='alertdialog'
          onOpenChange={e => {
            if (!e.open) {
              onDelTabClose();
            }
          }}>
          <Portal>

            <Dialog.Backdrop>
              <Dialog.Positioner>
                <Dialog.Content>
                  <Dialog.Header>Eliminar categoría</Dialog.Header>
                  <Dialog.Body>
                    ¿Eliminar la categoría <strong>"{toDelTab?.label}"</strong>?
                    Los contactos <strong>no se eliminarán</strong>, quedarán sin categoría.
                  </Dialog.Body>
                  <Dialog.Footer>
                    <Button ref={cancelDelTabRef} onClick={onDelTabClose}>Cancelar</Button>
                    <Button colorPalette="red" ml={3} onClick={handleDeleteTab}>Eliminar</Button>
                  </Dialog.Footer>
                </Dialog.Content>
              </Dialog.Positioner>
            </Dialog.Backdrop>

          </Portal>
</Dialog.Root>
      </Box>
    </AppLayout>
  );
}
